import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PMAvatar, PMBreadcrumb, PMButton, PMCard, PMCheckbox, PMChip, PMDialog, PMEmptyState, PMField, PMFormActions, PMIcon, PMMenu, PMProgress, PMSkeleton, PMTabs } from "@/ds/vendor";
import { Modal } from "@/ui/Modal";
import { useToast } from "@/ui/toast";
import { nameSuggestions, mentions, replaceWord, type Member } from "./entities";
import { loadSignature, saveSignature } from "./signature";
import { LANGUAGES, generateInLanguage } from "./reportActions";
import { FormsPanel } from "./FormsPanel";
import { useApp } from "@/app/AppContext";
import { useAncestors, useComments, useLocation, useLocationContacts, useObservations, usePeople, useReminders, useVisit, useVisitReport, type Observation } from "@/data/queries";
import { useMutations } from "@/data/mutations";
import { fetchVisitWeather, sky, tempU, windU, useAsync } from "@/data/weather";
import { levelDef } from "@/lib/levels";
import { ObservationCard } from "@/ui/ObservationCard";
import { MediaThumb, obsKind, VisitStatusBadge, type ObsKind } from "@/ui/visit";
import { TimePills } from "@/ui/FieldVisitCard";
import { visitStatus } from "@/ui/visitStatus";
import { LocationMap } from "@/ui/LocationMap";
import { SignaturePad } from "@/ui/SignaturePad";
import { Mark } from "@/ui/Mark";
import { Character } from "@/ui/Character";
import { avatarColor, clock, duration, fullName, relTime, shortDate } from "@/ui/format";
import { parseReport, toText, fromText, type ReportData } from "./report";
import "./visit.css";

const STAGES = ["pending", "media_downloading", "transcribing", "transcribed", "summarizing", "summarized", "extracting_reminders", "extracting_metrics", "completed"];
const STAGE_LABEL: Record<string, string> = { pending: "Queued", media_downloading: "Downloading media", media_downloaded: "Downloading media", transcribing: "Transcribing voice memos", transcribed: "Transcribed", summarizing: "Writing the summary", summarized: "Summary written", extracting_reminders: "Pulling out follow-ups", extracting_metrics: "Pulling out measurements", completed: "Done" };
const kindLabel = (k: ObsKind) => ({ photo: "Photo", audio: "Voice memo", text: "Note", video: "Video" })[k];

// The visit hub. One stable URL whatever the visit's state.
// Hero: satellite of the field with every observation pinned where it was taken,
// beside a photo mosaic. Then Started/Completed pills (+ weather at the time),
// and Report (a signed document) · Observations (linked to the map) · Discussion.
export function VisitPage() {
  const { visitId, teamId } = useParams({ from: "/t/$teamId/visits/$visitId" });
  const search = useSearch({ from: "/t/$teamId/visits/$visitId" });
  const nav = useNavigate();
  const { hierarchy, units } = useApp();
  const visit = useVisit(visitId);
  const v = visit.data;
  const obs = useObservations(visitId);
  const report = useVisitReport(visitId);
  const comments = useComments(visitId);
  const location = useLocation(v?.map_feature_id ?? "");
  const ancestors = useAncestors(v?.map_feature_id ?? "");
  const contacts = useLocationContacts(v?.map_feature_id ?? "");
  const people = usePeople();
  const tasks = useReminders();
  const m = useMutations();
  const [photo, setPhoto] = useState<Observation | null>(null);
  const [pin, setPin] = useState<string | null>(null);
  const loc = location.data;
  const lat = +(loc?.latitude || obs.data.find((o) => o.latitude)?.latitude || 0);
  const lng = +(loc?.longitude || obs.data.find((o) => o.longitude)?.longitude || 0);
  const wx = useAsync(lat && v?.started_at ? () => fetchVisitWeather(lat, lng, v.started_at!, units) : null, [lat, lng, v?.started_at, units]);

  if (!v) {
    return visit.isLoading ? <div className="page page--wide"><PMSkeleton shape="rect" height={340} radius={18} /><PMSkeleton shape="rect" height={420} radius={18} style={{ marginTop: 16 }} /></div>
      : <div className="page"><PMEmptyState title="Visit not found" description="It may have been deleted, or it isn't synced to this device." action={<PMButton onClick={() => nav({ to: "/t/$teamId/visits", params: { teamId } })}>All field visits</PMButton>} /></div>;
  }

  const d = levelDef(loc?.level, hierarchy.key);
  const status = visitStatus(v);
  const author = people.data.find((p) => p.user_id === v.user_id);
  const visitTasks = tasks.data.filter((t) => t.field_note_id === visitId);
  const photos = obs.data.filter((o) => obsKind(o) === "photo");
  const tab = search.tab || (report.data ? "report" : "observations");
  const setTab = (t: string) => nav({ to: ".", search: { tab: t }, replace: true } as any);
  const focusObs = (id: string) => {
    if (!obs.data.some((o) => o.id === id)) return;
    setPin(id);
    if (tab !== "observations") setTab("observations");
    setTimeout(() => document.getElementById(`obs-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  };

  const crumbs = [
    { label: hierarchy.levels[0].plural, href: `/t/${teamId}/locations` },
    ...ancestors.data.map((a) => ({ label: a.label || "Untitled", href: `/t/${teamId}/locations/${a.id}` })),
    ...(loc ? [{ label: loc.label || "Untitled", href: `/t/${teamId}/locations/${loc.id}` }] : []),
    { label: shortDate(v.started_at) },
  ];
  const mapItems = [
    ...(loc ? [{ id: loc.id, label: loc.label || "", geometry: loc.geometry, latitude: loc.latitude, longitude: loc.longitude }] : []),
    ...obs.data.filter((o) => o.latitude && o.longitude).map((o) => ({ id: o.id, label: `${clock(o.created_at)} · ${kindLabel(obsKind(o))}`, sub: (o.image_text_ai || o.body || o.raw_transcript || "").slice(0, 60), geometry: null, latitude: o.latitude, longitude: o.longitude, tone: obsKind(o) === "audio" ? "audio" as const : obsKind(o) === "photo" ? "info" as const : "primary" as const })),
  ];
  const wxs = wx.data ? sky(wx.data.code) : null;

  return (
    <div className="page page--wide visit">
      <PMBreadcrumb items={crumbs} onNavigate={(it: { href?: string }) => it.href && nav({ to: it.href } as any)} />

      <section className="vh-hero">
        <div className="vh-map">
          <LocationMap items={mapItems} height="100%" selectedId={pin} onSelect={focusObs} />
          <div className="vh-map-card pm-glass">
            <div className="row"><VisitStatusBadge v={v} /><span className="hint">{relTime(v.completed_at || v.started_at)}</span></div>
            <h1 className="vh-title">{loc?.label || v.title} — {shortDate(v.started_at)} visit</h1>
            <div className="vh-who">
              <PMAvatar name={fullName(author)} size={28} color={avatarColor(v.user_id)} />
              <span><strong>{fullName(author)}</strong><span className="muted"> · {d.emoji} {d.label}{loc?.description ? ` · ${loc.description}` : ""}</span></span>
            </div>
          </div>
          <div className="map-legend pm-glass"><span><i className="lg-photo" />Photo</span><span><i className="lg-audio" />Voice memo</span><span><i className="lg-text" />Note</span></div>
        </div>
        <div className="vh-mosaic">
          {photos.length ? photos.slice(0, 3).map((p, i) => (
            <button key={p.id} className="vh-tile" onClick={() => setPhoto(p)} aria-label="Open photo">
              <MediaThumb o={p} alt={i} />
              {(p.image_text_ai || p.whatsapp_message_content_ai) && <span className="vh-cap"><small>{clock(p.created_at)}</small>{p.image_text_ai || p.whatsapp_message_content_ai}</span>}
              {i === 2 && photos.length > 3 && <span className="vh-count">+{photos.length - 3} photos</span>}
            </button>
          )) : <div className="vh-nophotos"><Character name="farmer" size={96} title="No photos on this visit" line="Voice memos and notes are below." /></div>}
        </div>
      </section>

      <TimePills started={v.started_at} completed={v.completed_at} extra={
        <>
          {wxs && <div className="timepill timepill--wx"><span>{wxs.emoji} Weather at visit</span><strong>{Math.round(wx.data!.temp)}{tempU(units)} · {Math.round(wx.data!.wind)} {windU(units)}</strong></div>}
          {duration(v.started_at, v.completed_at) && <div className="timepill timepill--wx"><span><PMIcon name="route-outline" size={16} /> On site</span><strong>{duration(v.started_at, v.completed_at)}</strong></div>}
        </>
      } />

      {status === "processing" && <ProcessingBanner state={v.processing_state} />}
      {status === "in_progress" && <div className="banner banner--info">🚜 Still open on {author ? `${author.name}'s` : "a"} phone. New observations appear here as they sync.</div>}

      <div className="vh-body">
        <div className="visit-main">
          <div className="vh-tabs">
            <PMTabs value={tab} onChange={setTab} aria-label="Visit sections" tabs={[
              { id: "report", label: "Report", disabled: !report.data },
              { id: "observations", label: "Observations", count: obs.data.length },
              { id: "forms", label: "Forms" },
              { id: "discussion", label: "Discussion", count: comments.data.length },
            ]} />
            {report.data && <PMButton variant="outline" size="sm" icon="download" onClick={() => m.downloadReport(report.data!.id)}>PDF</PMButton>}
          </div>
          {tab === "report" && report.data && (
            <ReportPaper reportId={report.data.id} raw={report.data.report_data} approvedAt={report.data.approved_at} approverName={report.data.approver_name} signature={(report.data as any).signature_data}
              configName={report.data.config_name} generatedAt={report.data.generated_at} where={loc?.label || ""} parent={ancestors.data.at(-1)?.label}
              date={v.started_at} advisor={fullName(author)} contact={contacts.data[0]?.name ?? null} emails={contacts.data.map((c) => c.email).filter(Boolean) as string[]} visitId={visitId} />
          )}
          {tab === "report" && !report.data && <PMEmptyState title="No report yet" description={status === "processing" ? "The report is drafted as soon as the summary is written." : "A report is generated once the visit is completed on the phone."} />}
          {tab === "observations" && <Timeline obs={obs.data} loading={obs.isLoading} onOpenPhoto={setPhoto} highlight={pin} onPin={setPin} />}
          {tab === "forms" && <FormsPanel visitId={visitId} />}
          {tab === "discussion" && <Discussion visitId={visitId} locationId={v.map_feature_id} />}
        </div>

        <aside className="sticky-col">
          {v.ai_summary && (
            <PMCard eyebrow="AI summary" icon="leaf-outline">
              <p className="summary-text">{v.ai_summary}</p>
            </PMCard>
          )}
          <PMCard eyebrow="Follow-ups" icon="check-circle-outline" count={visitTasks.filter((t) => !t.completed_at).length}>
            {visitTasks.length ? (
              <ul className="checklist">
                {visitTasks.map((t) => (
                  <li key={t.id} className={t.completed_at ? "is-done" : ""}>
                    <PMCheckbox checked={!!t.completed_at} onChange={() => (t.completed_at ? m.reinstateReminder(t.id) : m.completeReminder(t.id, visitTasks.filter((x) => !x.completed_at && x.id !== t.id).length))} aria-label={t.details || "Task"} />
                    <button className="checklist-t checklist-open" onClick={() => nav({ to: "/t/$teamId/tasks", params: { teamId }, search: { task: t.id } } as any)}>{(t.details || "").split(/\n/)[0]}<small>{t.reminder_date ? `Due ${relTime(t.reminder_date)}` : "No date"}{t.ai_created ? " · from this visit" : ""}</small></button>
                  </li>
                ))}
              </ul>
            ) : <p className="hint">No follow-ups on this visit.</p>}
          </PMCard>
          {contacts.data.length > 0 && (
            <PMCard eyebrow={`${d.label} contacts`} icon="user-outline" count={contacts.data.length}>
              {contacts.data.map((c) => (
                <div key={c.id} className="contact-mini">
                  <PMAvatar name={c.name || "?"} size={36} color={avatarColor(c.id)} />
                  <div><strong>{c.name}</strong><div className="meta">{c.role}{c.phone && <><span className="sep" /><a href={`tel:${c.phone}`}>{c.phone}</a></>}</div>{c.email && <a className="hint" href={`mailto:${c.email}?subject=${encodeURIComponent(`${loc?.label} visit, ${shortDate(v.started_at)}`)}`}>Email {c.email}</a>}</div>
                </div>
              ))}
            </PMCard>
          )}
          <PMCard eyebrow="Captured" icon="camera-outline">
            <div className="captured-grid">
              {(["photo", "audio", "text"] as const).map((k) => (
                <button key={k} className={`captured-cell captured-cell--${k}`} onClick={() => setTab("observations")}>
                  <strong>{obs.data.filter((o) => obsKind(o) === k).length}</strong><span>{kindLabel(k)}s</span>
                </button>
              ))}
            </div>
          </PMCard>
        </aside>
      </div>

      <PMDialog open={!!photo} onClose={() => setPhoto(null)} title={photo ? `${loc?.label ?? "Photo"} · ${clock(photo.created_at)}` : ""} maxWidth={980}>
        {photo && (
          <div className="lightbox">
            <div className="lightbox-img"><MediaThumb o={photo} /></div>
            <div className="lightbox-strip">{photos.map((p, i) => <button key={p.id} className={p.id === photo.id ? "is-on" : ""} onClick={() => setPhoto(p)}><MediaThumb o={p} alt={i} /></button>)}</div>
            {(photo.image_text_ai || photo.whatsapp_message_content_ai) && <p className="obs-text"><span className="obs-label">AI read · </span>{photo.image_text_ai || photo.whatsapp_message_content_ai}</p>}
          </div>
        )}
      </PMDialog>
    </div>
  );
}

function ProcessingBanner({ state }: { state: string | null }) {
  const i = Math.max(0, STAGES.indexOf(state === "media_downloaded" ? "media_downloading" : state || "pending"));
  return (
    <div className="banner banner--processing" role="status">
      <div className="row"><strong>{STAGE_LABEL[state || "pending"] ?? "Processing"}</strong><span className="hint">Step {i + 1} of {STAGES.length}</span></div>
      <PMProgress variant="linear" value={Math.round(((i + 1) / STAGES.length) * 100)} tone="warning" label="Processing progress" />
    </div>
  );
}

function Timeline({ obs, loading, onOpenPhoto, highlight, onPin }: { obs: Observation[]; loading: boolean; onOpenPhoto: (o: Observation) => void; highlight: string | null; onPin: (id: string) => void }) {
  const [on, setOn] = useState<Record<ObsKind, boolean>>({ photo: true, audio: true, text: true, video: true });
  const shown = obs.filter((o) => on[obsKind(o)]);
  if (loading && !obs.length) return <div className="skeleton-stack">{[0, 1, 2].map((i) => <PMSkeleton key={i} shape="rect" height={110} />)}</div>;
  if (!obs.length) return <PMEmptyState title="No observations yet" description="Photos, voice memos and notes from the phone land here as they sync." />;
  const count = (k: ObsKind) => obs.filter((o) => obsKind(o) === k).length;
  return (
    <div className="timeline">
      <div className="toolbar">
        <PMChip label={`Photos · ${count("photo")}`} selected={on.photo} dotColor="var(--pm-data-photo)" onToggle={() => setOn((s) => ({ ...s, photo: !s.photo }))} />
        <PMChip label={`Voice memos · ${count("audio")}`} selected={on.audio} dotColor="var(--pm-data-audio)" onToggle={() => setOn((s) => ({ ...s, audio: !s.audio }))} />
        <PMChip label={`Notes · ${count("text")}`} selected={on.text} dotColor="var(--pm-data-text)" onToggle={() => setOn((s) => ({ ...s, text: !s.text }))} />
        <span className="spacer" /><span className="hint">Pins on the map jump to their observation</span>
      </div>
      <ol className="timeline-list">
        {shown.map((o, i) => (
          <li key={o.id} className={highlight === o.id ? "is-pinned" : ""} onMouseEnter={() => o.latitude && onPin(o.id)}>
            <ObservationCard o={o} index={i} onOpenPhoto={onOpenPhoto} />
          </li>
        ))}
      </ol>
    </div>
  );
}

// The report as the document the client receives: letterhead, visit facts,
// editable sections, and a signature block that becomes the approval.
function ReportPaper(p: { reportId: string; raw: string | null; approvedAt: string | null; approverName: string | null; signature: string | null; configName: string | null; generatedAt: string | null; where: string; parent?: string | null; date: string | null; advisor: string; contact: string | null; emails: string[]; visitId: string }) {
  const data = useMemo(() => parseReport(p.raw), [p.raw]);
  const m = useMutations();
  const { teamName, userEmail, userId } = useApp();
  const me = usePeople().data.find((x) => x.user_id === userId);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [signing, setSigning] = useState(false);
  const [name, setName] = useState("");
  const [sig, setSig] = useState<string | null>(null);
  const [justSigned, setJustSigned] = useState(false);
  const [saveDefault, setSaveDefault] = useState(true);
  const [saved, setSaved] = useState(loadSignature);
  const [sharing, setSharing] = useState(false);
  const [ignored, setIgnored] = useState<string[]>([]);
  const [lang, setLang] = useState("english");
  const { mode } = useApp();
  const toast = useToast();
  const people = usePeople().data;
  const roster: Member[] = useMemo(() => people.map((x) => ({ id: x.user_id, first: x.name || "", last: x.last_name || "", full: fullName(x) })), [people]);
  const dirty = Object.keys(draft).length > 0;
  const fallback = userEmail.split("@")[0];
  useEffect(() => { if (signing && me && (!name || name === fallback)) setName(fullName(me)); }, [signing, me, name, fallback]);

  if (!data) return <PMEmptyState title="Report format not recognized" description="This report was generated in a format v2 doesn't render yet. Open it on the phone or in classic." />;

  const openSign = () => { setName(name || (me ? fullName(me) : fallback)); setSigning(true); };
  const save = async () => {
    const next: ReportData = { ...data, sections: data.sections.map((s, i) => (i in draft ? { ...s, content: fromText(s.content, draft[i]) } : s)) };
    if (await m.saveReport(p.reportId, next)) setDraft({});
  };
  const sign = async () => {
    if (!sig || !name.trim()) return;
    if (dirty) await save();
    if (await m.approveReport(p.reportId, name.trim(), sig, p.where)) {
      if (saveDefault) { saveSignature({ name: name.trim(), data: sig }); setSaved({ name: name.trim(), data: sig }); }
      setSigning(false); setJustSigned(true); setTimeout(() => setJustSigned(false), 2400);
    }
  };
  // One-tap approve & distribute: saved signature → sign → share sheet.
  const approveAndShare = async () => {
    if (!saved) return openSign();
    if (dirty) await save();
    if (await m.approveReport(p.reportId, saved.name, saved.data, p.where)) { setJustSigned(true); setTimeout(() => setJustSigned(false), 2400); setSharing(true); }
  };
  const translate = async (v: string) => {
    setLang(v);
    if (v === "english") return;
    if (mode === "demo") { toast({ variant: "info", title: "Translation runs on the live backend", message: "The demo team has no report generator behind it." }); return; }
    try {
      const r = await generateInLanguage(p.reportId, v);
      toast({ variant: "success", title: r.status === "exists" ? "Translation already exists" : "Translation started", message: "The translated report appears in Reports when it is ready." });
    } catch (e: any) {
      toast({ variant: "error", title: "Translation unavailable", message: e?.body?.error || "Try again in a moment." });
    }
  };
  const sectionText = (i: number, s: { content: unknown }) => (i in draft ? draft[i] : toText(s.content));
  const allText = data.sections.map((s, i) => sectionText(i, s)).join("\n");
  const suggestions = nameSuggestions(allText, roster).filter((x) => !ignored.includes(x.wrong));
  const routed = mentions(allText, roster);
  const applyName = (wrong: string, right: string) => setDraft((d) => {
    const next = { ...d };
    data.sections.forEach((s, i) => { const t = sectionText(i, s); if (t.includes(wrong)) next[i] = replaceWord(t, wrong, right); });
    return next;
  });
  const link = `${location.origin}/t/${location.pathname.split("/")[2]}/visits/${p.visitId}?tab=report`;
  const mailto = `mailto:${p.emails.join(",")}?subject=${encodeURIComponent(`${p.where} field visit report`)}&body=${encodeURIComponent(`The signed report for ${p.where} is ready:\n${link}`)}`;

  return (
    <div className="paper-wrap">
      <div className="paper-bar">
        <span className="hint">{p.configName || "Report"} · generated {relTime(p.generatedAt)}{p.approvedAt ? "" : " · click any section to edit"}</span>
        <span className="spacer" />
        {dirty && <PMButton variant="text" size="sm" onClick={() => setDraft({})}>Discard</PMButton>}
        {dirty && <PMButton variant="outline" size="sm" onClick={save}>Save changes</PMButton>}
        <PMMenu label="Language" icon="chat-dots" value={lang} align="right" options={LANGUAGES.map((l) => ({ value: l.value, label: l.label }))} onChange={(v: string | string[]) => translate(v as string)} />
        {p.approvedAt && <PMButton variant="outline" size="sm" icon="external-link" onClick={() => setSharing(true)}>Share</PMButton>}
        {!p.approvedAt && saved && <PMButton variant="outline" size="sm" icon="edit" onClick={openSign}>Sign report</PMButton>}
        {!p.approvedAt && (saved
          ? <PMButton size="sm" icon="check-circle" onClick={approveAndShare}>Approve &amp; share</PMButton>
          : <PMButton size="sm" icon="edit" onClick={openSign}>Sign report</PMButton>)}
      </div>
      {(suggestions.length > 0 || routed.length > 0) && !p.approvedAt && (
        <div className="entity-bar" role="status">
          {suggestions.map((x) => (
            <span className="entity-sug" key={x.wrong}>
              <PMIcon name="user-outline" size={16} />
              <span>Did you mean <strong>{x.member.full}</strong>? <span className="subtle">“{x.wrong}”</span></span>
              <button className="linkbtn" onClick={() => applyName(x.wrong, x.member.first)}>Apply</button>
              <button className="linkbtn entity-ignore" onClick={() => setIgnored((l) => [...l, x.wrong])}>Ignore</button>
            </span>
          ))}
          {routed.map((r) => (
            <span className={`entity-route ${r.member ? "" : "is-unknown"}`} key={r.raw}>
              {r.member ? <>Task will route to <strong>{r.member.full}</strong></> : <>{r.raw} isn't on the team</>}
            </span>
          ))}
        </div>
      )}
      <article className="paper">
        <header className="paper-head">
          <div className="row"><Mark size={30} /><div><div className="paper-team">{teamName}</div><div className="hint">Field visit report</div></div></div>
          {p.approvedAt ? <span className={`paper-stamp ${justSigned ? "pm-pop" : ""}`}>✓ Signed</span> : <span className="paper-draft">Draft</span>}
        </header>
        <dl className="paper-facts">
          <div><dt>Location</dt><dd>{p.where}{p.parent ? `, ${p.parent}` : ""}</dd></div>
          <div><dt>Date</dt><dd>{p.date ? new Date(p.date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "—"}</dd></div>
          <div><dt>Advisor</dt><dd>{p.advisor}</dd></div>
          {p.contact && <div><dt>Prepared for</dt><dd>{p.contact}</dd></div>}
        </dl>
        {data.sections.map((s, i) => {
          const value = i in draft ? draft[i] : toText(s.content);
          const list = Array.isArray(s.content);
          return (
            <section className={`paper-sec ${i in draft ? "is-edited" : ""}`} key={i}>
              <h4>{s.heading}</h4>
              <AutoText className={`paper-edit ${list ? "paper-edit--list" : ""}`} value={value} onChange={(t) => setDraft((d) => ({ ...d, [i]: t }))} label={s.heading} />
            </section>
          );
        })}
        <footer className="paper-sign">
          {p.approvedAt ? (
            <div className="paper-sigblock">
              {p.signature && p.signature.startsWith("data:image") ? <img src={p.signature} alt={`Signature of ${p.approverName}`} /> : <span className="paper-sigtext">{p.approverName}</span>}
              <div className="paper-sigline">{p.approverName} · {shortDate(p.approvedAt)}</div>
            </div>
          ) : (
            <button className="paper-sigblock paper-sigblock--empty" onClick={openSign}>
              <span>Sign here</span><div className="paper-sigline">Approver signature</div>
            </button>
          )}
          {p.approvedAt && <p className="hint">Edits to a signed report are saved as a new version. The signed version stays as the record.</p>}
        </footer>
      </article>

      <PMDialog open={signing} onClose={() => setSigning(false)} title="Sign and approve" subtitle="The signed PDF is what the client receives." maxWidth={560}
        footer={<PMFormActions onCancel={() => setSigning(false)} onConfirm={sign} confirmLabel="✓ Sign and approve" confirmDisabled={!sig || !name.trim()} />}>
        <div className="sign-form">
          <PMField label="Approver name" value={name} onChange={(e: any) => setName(e.target.value)} required />
          <SignaturePad onChange={setSig} />
          <PMCheckbox checked={saveDefault} onChange={() => setSaveDefault((x) => !x)} label="Save as my default signature" />
          {dirty && <p className="hint">Unsaved edits are saved before signing.</p>}
        </div>
      </PMDialog>

      <Modal open={sharing} onClose={() => setSharing(false)} size="sm" title="Share report" subtitle={`${p.where} · signed`}>
        <div className="share-list">
          <button className="share-row" onClick={() => { navigator.clipboard?.writeText(link).then(() => toast({ variant: "success", title: "Link copied" })).catch(() => {}); }}>
            <PMIcon name="link-outline" size={20} /><span><strong>Copy link</strong><small>Team members open it in PropelMapper</small></span>
          </button>
          <a className="share-row" href={mailto}>
            <PMIcon name="chat-dots-outline" size={20} /><span><strong>Email</strong><small>{p.emails.length ? p.emails.join(", ") : "No contact email on this location"}</small></span>
          </a>
          <button className="share-row" onClick={() => m.downloadReport(p.reportId)}>
            <PMIcon name="download-outline" size={20} /><span><strong>Download PDF</strong><small>The signed document</small></span>
          </button>
        </div>
      </Modal>
    </div>
  );
}

function AutoText({ value, onChange, className, label }: { value: string; onChange: (v: string) => void; className: string; label: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} className={className} value={value} rows={1} onChange={(e) => onChange(e.target.value)} aria-label={label} />;
}

function Discussion({ visitId, locationId }: { visitId: string; locationId: string | null }) {
  const comments = useComments(visitId);
  const people = usePeople();
  const m = useMutations();
  const [text, setText] = useState("");
  const post = async () => {
    if (!text.trim() || !locationId) return;
    if (await m.addComment({ locationId, visitId, content: text.trim() })) setText("");
  };
  return (
    <div className="discussion">
      {comments.data.length === 0 && <Character name="chicken" size={80} title="No comments yet" line="Notes for the team about this visit live here." />}
      {comments.data.map((c) => {
        const p = people.data.find((x) => x.user_id === c.user_id);
        return (
          <div className="comment" key={c.id}>
            <PMAvatar name={fullName(p)} size={32} color={avatarColor(c.user_id)} />
            <div className="comment-bubble">
              <div className="meta"><strong>{fullName(p)}</strong><span className="sep" /><span>{relTime(c.created_at)}</span>{c.ai_generated ? <><span className="sep" /><span>AI suggestion</span></> : null}</div>
              <p className="comment-text">{c.content}</p>
            </div>
          </div>
        );
      })}
      <div className="composer">
        <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment for the team" rows={2} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post(); }} aria-label="Comment" />
        <PMButton size="sm" disabled={!text.trim() || !locationId} onClick={post}>Post</PMButton>
      </div>
    </div>
  );
}
